import React, { useCallback, useEffect, useState } from 'react';
import propTypes from 'prop-types';
import { Controller, useForm } from 'react-hook-form';
import classnames from 'classnames';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import TextField from '@material-ui/core/TextField';
import IconSunny from 'assets/images/icon-sunny.svg';
import IconMoon from 'assets/images/icon-moon.svg';
import PageLoader from '../../../../../PageLoader2';
import styles from './styles';
import { getDateFromHours } from 'helpers/_date-helpers';
import AlertModal from '../../../../../AlertModal/AlertModal';
import {
  useSheetFormStyles,
  useResourcesFormStyles,
  useSheetDetailsStyles,
} from '../../../../hooks';
import { isIOS, Option } from '../../../../../Option';
import OverlappingModal from '../../../../../OverlapsingModal';
import { useDispatch, useSelector } from 'react-redux';
import { updateSheetAddResource } from 'store/schedule/schedulePageOperation';
import {
  generateHoursObject,
  getEffectiveStartTime,
  isSelectedTimeValid,
  isSelectedTravelTimeTimeValid,
  isStartTimeEarlierOnJob,
} from 'helpers/helpers';
import { patchWorklogJobTime } from 'store/currentWorklog/operations';
import { useWeekStart } from 'hooks/useWeekStart';
import Checkbox from '@material-ui/core/Checkbox';
import { convertTimeObject, convertTo24HourFormat } from 'common/dateFormatConfig';
import { useTimeSettingsFormat } from 'common/useTimeSettingsFormat';
import { useCanEditWorklogCrewAndTime } from 'common/hooks/use-can-edit-worklog';
import currentWorklogActions from 'store/currentWorklog/actions';
import { isTimeChanged } from 'components/WorklogDetails/helpers';
import { add, differenceInDays, set } from 'date-fns';
import DayInput from 'components/DayInput';
import DurationInput from 'components/DurationInput';
import TimeInput from 'components/TimeInput';

const useStyles = makeStyles(styles);

const defaultModalState = { isOpen: false, text: '' };

const ON_JOB_START = ['dayStart', 'hourStart', 'minuteStart', 'amPmStart'];
const ON_JOB_END = ['dayEnd', 'hourEnd', 'minuteEnd', 'amPmEnd'];
const TRAVEL_TIME = [
  'travelTimeToHours',
  'travelTimeToMinutes',
  'travelTimeFromHours',
  'travelTimeFromMinutes',
];
const START_TIME = {
  day: 'startTimeDay',
  hour: 'startTimeHours',
  minute: 'startTimeMinutes',
  amPm: 'startTimeDayPart',
};

const isNumeric = (value) => value === '' || /^[0-9]+$/.test(value);
const RAIN_PAY_RULES = {
  rainPayHours: { isNumeric, lessThan24: (value) => value === '' || parseInt(value) <= 24 },
  rainPayMinutes: { isNumeric, lessThan60: (value) => value === '' || parseInt(value) <= 59 },
};
const RAIN_PAY = Object.keys(RAIN_PAY_RULES);

const JOB_TIME_ERROR = 'Start hours should be earlier than end hours';

const getHourValues = (values) => ({
  dayStart: values.dayStart,
  hourStart: values.hourStart,
  minuteStart: values.minuteStart,
  amPmStart: values.amPmStart,
  dayEnd: values.dayEnd,
  hourEnd: values.hourEnd,
  minuteEnd: values.minuteEnd,
  amPmEnd: values.amPmEnd,
  travelTimeToHours: values.travelTimeToHours,
  travelTimeToMinutes: values.travelTimeToMinutes,
  travelTimeFromHours: values.travelTimeFromHours,
  travelTimeFromMinutes: values.travelTimeFromMinutes,
});

export const TimeForm = ({ closeDrawer, openSnackbar }) => {
  const getTimeFormatHook = useTimeSettingsFormat();
  const is12Format = getTimeFormatHook().is12Format;

  const { weekDayFromO } = useWeekStart();

  const dispatch = useDispatch();
  const locationSettings = useSelector((state) => state.personalProfile?.organizationLocation?.settings);
  const user = useSelector((store) => store.personalProfile.user) || {};
  const sheet = useSelector((store) => store.currentWorklog.worklogSheet.sheet);

  const [infoModal, setInfoModal] = useState(defaultModalState);
  const [shift, setShift] = useState('');
  const [startTime, setStartTime] = useState({
    day: '',
    hour: '',
    minute: '',
    amPm: '',
  });
  const [isInitialized, setIsInitialized] = useState(false);

  const [overlappingModal, setOverlappingModal] = useState(null);

  const [isAccessGrantedEditStartTime, setIsAccessGrantedEditStartTime] = useState(false);

  const sheetFormClasses = useSheetFormStyles();
  const resourcesFormClasses = useResourcesFormStyles();
  const sheetDetailsClasses = useSheetDetailsStyles();
  const classes = useStyles();

  const {
    handleSubmit,
    setValue,
    control,
    formState: { errors },
    setError,
    getValues,
    clearErrors,
    register,
    unregister,
    trigger,
    watch,
  } = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      travelTimeToHours: '',
      travelTimeToMinutes: '',
      travelTimeFromHours: '',
      travelTimeFromMinutes: '',
    },
  });

  const getInitialHoursOnUpdate = (sheet) => {
    const { hours, createdAt } = sheet;
    return generateHoursObject(hours.start, hours.end, createdAt, getTimeFormatHook);
  };

  const isMiscStipendEnabled = watch('miscStipend');
  const showMiscStipend = Boolean(
    isMiscStipendEnabled && locationSettings?.timeclock && locationSettings?.miscStipend
  );
  const time = watch([...ON_JOB_START, ...ON_JOB_END, ...TRAVEL_TIME, ...RAIN_PAY]);

  useEffect(() => {
    register({ name: 'timeOfDay' });
    register({ name: 'startTimeDay' });
    register({ name: 'startTimeHours' });
    register({ name: 'startTimeMinutes' });
    register({ name: 'startTimeDayPart' });
    register({ name: 'travelTimeOnly' });
  }, [register]);

  useEffect(() => {
    ON_JOB_START.forEach((name) =>
      register(
        { name },
        { validate: { startTimeEarlierOnJobValidation: startTimeEarlierOnJobValidation(name) } }
      )
    );
    ON_JOB_END.forEach((name) => register({ name }, { validate: { dateValid } }));
    TRAVEL_TIME.forEach((name) => register({ name }, { validate: { travelTimeValid } }));
  });

  useEffect(() => {
    if (!showMiscStipend) return undefined;
    Object.entries(RAIN_PAY_RULES).forEach(([name, validate]) => register({ name }, { validate }));
    setValue('rainPayHours', sheet.rainPay?.hours || '0');
    setValue('rainPayMinutes', sheet.rainPay?.minutes || '0');
    return () => unregister(RAIN_PAY);
  }, [showMiscStipend]);

  useEffect(() => {
    if (!sheet || !Object.keys(sheet).length || isInitialized) return;
    if (sheet.timeOfDay) {
      setShift(sheet.timeOfDay);
      setValue('timeOfDay', sheet.timeOfDay);
    }
    setValue('travelTimeOnly', sheet.travelTimeOnly);

    if (sheet.hours) {
      const hours = getInitialHoursOnUpdate(sheet);
      Object.keys(hours).map((key) => setValue(key, hours[key]));
    }
    if (sheet.startTime) {
      const effectiveStartTime = getEffectiveStartTime(sheet.startTime, sheet.startTimeDate);
      const { hour, minute, amPm } = convertTimeObject(
        { hour: effectiveStartTime.hour, minute: effectiveStartTime.minute },
        is12Format
      );
      const day = sheet.startTimeDate
        ? `${differenceInDays(new Date(sheet.startTimeDate), new Date(sheet.createdAt))}`
        : '0';
      setStartTime({
        day: day,
        hour: String(Number(hour)),
        minute,
        amPm,
      });
      setValue('startTimeDay', day);
      setValue('startTimeHours', hour);
      setValue('startTimeMinutes', minute);
      setValue('startTimeDayPart', amPm);
    }
    if (sheet.hours.travelTimeTo || sheet.hours.travelTimeFrom) {
      setValue('travelTimeToHours', sheet.hours.travelTimeTo?.hours || '0');
      setValue('travelTimeToMinutes', sheet.hours.travelTimeTo?.minutes || '0');
      setValue('travelTimeFromHours', sheet.hours.travelTimeFrom?.hours || '0');
      setValue('travelTimeFromMinutes', sheet.hours.travelTimeFrom?.minutes || '0');
    }
    if (sheet.rainPay) {
      setValue('miscStipend', sheet.isRainPayEnabled || false);
      setValue('rainPayHours', sheet.rainPay?.hours || '0');
      setValue('rainPayMinutes', sheet.rainPay?.minutes || '0');
      setValue('miscStipendDescription', sheet.rainPay?.description || '');
    }
    setIsAccessGrantedEditStartTime(hasStartTimeAccess);
    setIsInitialized(true);
  }, [sheet, isInitialized]);

  const onSubmit = async (values) => {
    if (errors && Object.keys(errors).length) return;
    const hoursValues = getHourValues(values);
    const startTimeHour = is12Format
      ? convertTo24HourFormat(values.startTimeHours, values.startTimeDayPart)
      : values.startTimeHours;
    const startTimeDate = set(
      add(new Date(sheet.createdAt), { days: +(values.startTimeDay) }),
      { hours: startTimeHour, minutes: values.startTimeMinutes },
    );

    const submitData = {
      startTime: {
        hour: startTimeHour,
        minute: values.startTimeMinutes,
      },
      startTimeDate,
      timeOfDay: values.timeOfDay,
      hours: getDateFromHours(hoursValues, sheet.createdAt, getTimeFormatHook),
      travelTimeOnly: values.travelTimeOnly,
      rainPay: {
        hours: values.rainPayHours || '0',
        minutes: values.rainPayMinutes || '0',
        description: values.miscStipendDescription || '',
      },
      isRainPayEnabled: values.miscStipend,
    };

    if (values.travelTimeOnly) {
      delete submitData.hours.start;
      delete submitData.hours.end;
    }

    if (!locationSettings?.miscStipend) {
      delete submitData.isRainPayEnabled;
      delete submitData.rainPay;
    }

    try {
      const updateTime = async () => {
        await dispatch(patchWorklogJobTime(submitData, sheet._id));
        await dispatch(updateSheetAddResource(sheet._id, 1));
        closeDrawer();
      }

      if (!isTimeChanged(sheet, submitData)) return closeDrawer();
      
      await updateTime();
      if (sheet.submittedAt) {
        dispatch(currentWorklogActions.submittedChangesModalWlChanged());
      }
    } catch (e) {
      if (e.response && e.response.data.type === 'sheet-time-overlapse') {
        setOverlappingModal({
          reason: e.response.data.message,
          data: e.response.data.data,
          type: e.response.data.resourceType,
        });
      }
      if (e.response && e.response.data.type === 'timeoff-time-overlapse') {
        setInfoModal({ isOpen: true, text: e.response.data.message });
      } else {
        let message = e.response?.data?.message || e.message;
        if (Array.isArray(message)) message = message.map(m => m.startsWith('hours: ') ? m.slice(7) : m).join('\n');
        openSnackbar && openSnackbar('error', message);
      }
    }
  };

  const closeModal = () => setInfoModal(defaultModalState);
  const closeOverlappingModal = () => setOverlappingModal(null);

  const dateValid = () =>
    getValues('travelTimeOnly') ||
    isSelectedTimeValid(
      { ...getValues([...ON_JOB_START, ...ON_JOB_END]), isShowHours: true },
      getTimeFormatHook
    ).isValid;

  const startTimeEarlierOnJobValidation = useCallback((fieldName) => () => {
    const values = getValues();

    if (values.travelTimeOnly) {
      return true;
    }

    const hoursValues = {
      ...values,
      onJobDay: values.dayStart,
      onJobHours: values.hourStart,
      onJobMinutes: values.minuteStart,
      onJobDayPart: values.amPmStart
    }
    const { errorsOnFields } = isStartTimeEarlierOnJob(hoursValues, getTimeFormatHook, sheet);

    const validationErrors = {
      dayStart: errorsOnFields.onJobDay,
      hourStart: errorsOnFields.onJobHours,
      minuteStart: errorsOnFields.onJobMinutes,
      amPmStart: errorsOnFields.onJobDayPart,
      startTimeDay: errorsOnFields.startTimeDay,
      startTimeHours: errorsOnFields.startTimeHours,
      startTimeMinutes: errorsOnFields.startTimeMinutes,
      startTimeDayPart: errorsOnFields.startTimeDayPart,
    }

    if (!Object.getOwnPropertyNames(errorsOnFields)?.length) {
      Object.keys(validationErrors).forEach(field => {
        if (errors[field]?.type === 'startTimeEarlierOnJobValidation') {
          clearErrors(field);
        }
      })
      return true;
    }

    Object.entries(validationErrors).forEach(
      ([field, errorMessage]) => setError(field, { type: 'startTimeEarlierOnJobValidation', message: errorMessage })
    );
    return validationErrors[fieldName];
  }, [getValues, errors])

  const travelTimeValid = () => {
    const values = getValues();
    const hoursValues = getHourValues(values);
    const { isValid, errorsOnFields } = isSelectedTravelTimeTimeValid(hoursValues, true);
    clearErrors();
    errorsOnFields.map((field) => setError(field, 'dateValid'));

    return isValid;
  };

  const handleShiftChange = (e) => {
    const { value } = e.target;
    setShift(value);
    setValue('timeOfDay', value);

    const defaultTime = value === 'Day' ? 'defaultDayTime' : 'defaultNightTime';
    if (!locationSettings[defaultTime]) return;
    const { hour, minute, amPm } = convertTimeObject(locationSettings[defaultTime], is12Format);
    setStartTime({ amPm, hour: String(Number(hour)), minute: minute, day: '0' });
    setValue('startTimeDay', '0');
    setValue('startTimeHours', String(Number(hour)));
    setValue('startTimeMinutes', minute);
    setValue('startTimeDayPart', amPm);
    startTimeEarlierOnJobValidation()();
  };

  const handleStartTimeChange = (parts) => {
    Object.entries(parts).forEach(([part, value]) => setValue(START_TIME[part], value));
    setStartTime((prev) => ({ ...prev, ...parts }));
    startTimeEarlierOnJobValidation()();
  };

  const setParts = (parts, validated = Object.keys(parts)) => {
    Object.entries(parts).forEach(([name, value]) => setValue(name, value));
    trigger(validated);
  };

  const setJobTime = (parts) =>
    setParts(parts, [...ON_JOB_START.filter((name) => name in parts), ...ON_JOB_END]);

  const hasError = (names) => names.some((name) => errors[name]);
  const isJobTimeInvalid = hasError(ON_JOB_END);

  const durationProps = (name) => ({
    value: { hours: time[`${name}Hours`], minutes: time[`${name}Minutes`] },
    onChange: ({ hours, minutes }) =>
      setParts({ [`${name}Hours`]: String(hours), [`${name}Minutes`]: String(minutes) }),
    error: hasError([`${name}Hours`, `${name}Minutes`]),
    maxHours: 24,
    className: classes.field,
  });

  const hasStartTimeAccess = useCanEditWorklogCrewAndTime(sheet);

  return (
    <>
      <div className={classes.formWrapper} role="presentation">
        {!Object.keys(sheet).length ? (
          <PageLoader loading>
            <div />
          </PageLoader>
        ) : (
          <PageLoader loading={false} small>
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className={resourcesFormClasses.formFlex}>
                <div>
                  <Typography variant="h3" style={{ marginBottom: '26px' }}>
                    Edit On Job Time
                  </Typography>

                  <IconButton
                    className={classnames(classes.closeButton, resourcesFormClasses.closeButton)}
                    onClick={closeDrawer}
                    aria-label="Close"
                    disableTouchRipple
                  >
                    <CloseIcon />
                  </IconButton>

                  <Grid container spacing={4}>
                    <Grid item xs={12} lg={6} className={sheetDetailsClasses.zeroPaddingTop}>
                      <label htmlFor="timeOfDay">
                        <Typography variant="body1" color="textSecondary">
                          Shift:
                        </Typography>
                        <div style={{ marginRight: '10px' }}>
                          <TextField
                            select
                            variant="outlined"
                            inputProps={{ 'aria-label': 'Shift' }}
                            fullWidth
                            onChange={handleShiftChange}
                            value={shift}
                            SelectProps={{
                              native: isIOS,
                            }}
                          >
                            <Option key={0} value="Day">
                              {isIOS ? (
                                '🌤 '
                              ) : (
                                <img
                                  src={IconSunny}
                                  alt="sunny"
                                  className={sheetDetailsClasses.shiftImage}
                                />
                              )}
                              Day
                            </Option>
                            <Option key={1} value="Night">
                              {isIOS ? (
                                '🌙 '
                              ) : (
                                <img
                                  src={IconMoon}
                                  alt="moon"
                                  className={sheetDetailsClasses.shiftImage}
                                />
                              )}
                              Night
                            </Option>
                          </TextField>
                        </div>
                      </label>
                    </Grid>
                    <Grid item xs={12} lg={6} className={sheetDetailsClasses.zeroPaddingTop}>
                      <label>
                        <Typography variant="body1" color="textSecondary">
                          Start Time:
                        </Typography>
                      </label>
                      <div className={classes.fields}>
                        <DayInput
                          value={startTime.day}
                          onChange={(day) => handleStartTimeChange({ day })}
                          baseDate={sheet.createdAt}
                          error={Boolean(errors.startTimeDay)}
                          disabled={!isAccessGrantedEditStartTime}
                        />
                        <TimeInput
                          value={startTime}
                          onChange={handleStartTimeChange}
                          error={hasError([
                            'startTimeHours',
                            'startTimeMinutes',
                            'startTimeDayPart',
                          ])}
                          disabled={!isAccessGrantedEditStartTime}
                        />
                      </div>
                      {errors.startTimeHours && errors?.startTimeHours?.type === 'startTimeEarlierOnJobValidation' ? (
                        <div className={classes.noPaddingError}>{errors.startTimeHours?.message}</div>
                      ) : null}
                    </Grid>

                    <Grid
                      container
                      direction="row"
                      alignItems="center"
                      item
                      xs={12}
                      lg={4}
                      className={sheetDetailsClasses.zeroPaddingTop}
                    >
                      <label style={{ display: 'flex', alignItems: 'center' }}>
                        <Controller
                          name="travelTimeOnly"
                          control={control}
                          defaultValue={false}
                          type="checkbox"
                          render={(value) => {
                            return (
                              <Checkbox
                                {...value}
                                checked={value?.value}
                                onChange={(e) => {
                                  clearErrors();
                                  value.onChange(e.target.checked);
                                }}
                                // onChange={(e) => setValue('travelTimeOnly', e.target.checked)}

                                color="primary"
                                className={classes.primaryCheckbox}
                              />
                            );
                          }}
                        />
                        <Typography variant="body1" className={classes.primaryAdd}>
                          {isMiscStipendEnabled ? 'Omit Job Time' : 'Travel Time Only'}
                        </Typography>
                      </label>
                    </Grid>
                    {locationSettings?.timeclock && locationSettings?.miscStipend && (
                      <Grid
                        container
                        direction="row"
                        alignItems="center"
                        item
                        xs={12}
                        lg={4}
                        className={sheetDetailsClasses.zeroPaddingTop}
                      >
                        <label style={{ display: 'flex', alignItems: 'center' }}>
                          <Controller
                            name="miscStipend"
                            control={control}
                            defaultValue={false}
                            type="checkbox"
                            render={(value) => {
                              return (
                                <Checkbox
                                  {...value}
                                  checked={value?.value}
                                  onChange={(e) => {
                                    clearErrors();
                                    value.onChange(e.target.checked);
                                  }}
                                  // onChange={(e) => setValue('miscStipend', e.target.checked)}

                                  color="primary"
                                  className={classes.primaryCheckbox}
                                />
                              );
                            }}
                          />
                          <Typography variant="body1" className={classes.primaryAdd}>
                            Misc. Stipend
                          </Typography>
                        </label>
                      </Grid>
                    )}

                    <Grid container spacing={4} style={{ position: 'relative', margin: 0 }}>
                      <Grid item xs={12} lg={6} className={sheetDetailsClasses.zeroPaddingTop}>
                        <label>
                          <Typography variant="body1" color="textSecondary">
                            On Job Time:
                          </Typography>
                        </label>
                        <div className={classes.fields}>
                          <DayInput
                            value={time.dayStart}
                            onChange={(dayStart) => setJobTime({ dayStart })}
                            baseDate={sheet.createdAt}
                            error={Boolean(errors.dayStart) || isJobTimeInvalid}
                            disabled={getValues().travelTimeOnly}
                          />
                          <TimeInput
                            value={{
                              hour: time.hourStart,
                              minute: time.minuteStart,
                              amPm: time.amPmStart,
                            }}
                            onChange={({ hour, minute, amPm }) =>
                              setJobTime({ hourStart: hour, minuteStart: minute, amPmStart: amPm })
                            }
                            error={
                              hasError(['hourStart', 'minuteStart', 'amPmStart']) || isJobTimeInvalid
                            }
                            disabled={getValues().travelTimeOnly}
                          />
                        </div>
                        {errors.hourStart && errors?.hourStart?.type === 'startTimeEarlierOnJobValidation' ? (
                          <div className={classes.noPaddingError}>{errors.hourStart?.message}</div>
                        ) : null}
                        {isJobTimeInvalid && (
                          <div className={classes.noPaddingError}>{JOB_TIME_ERROR}</div>
                        )}
                      </Grid>
                      <div className={classes.dash}>
                        <Typography variant="body1" color="textSecondary">
                          &mdash;
                        </Typography>
                      </div>
                      <Grid item xs={12} lg={6} className={sheetDetailsClasses.zeroPaddingTop}>
                        <label>
                          <Typography variant="body1" color="textSecondary">
                            &nbsp;
                          </Typography>
                        </label>
                        <div className={classes.fields}>
                          <DayInput
                            value={time.dayEnd}
                            onChange={(dayEnd) => setJobTime({ dayEnd })}
                            baseDate={sheet.createdAt}
                            error={isJobTimeInvalid}
                            disabled={getValues().travelTimeOnly}
                          />
                          <TimeInput
                            value={{ hour: time.hourEnd, minute: time.minuteEnd, amPm: time.amPmEnd }}
                            onChange={({ hour, minute, amPm }) =>
                              setJobTime({ hourEnd: hour, minuteEnd: minute, amPmEnd: amPm })
                            }
                            error={isJobTimeInvalid}
                            disabled={getValues().travelTimeOnly}
                          />
                        </div>
                      </Grid>
                    </Grid>

                    <Grid container lg={12} spacing={4} style={{ position: 'relative', margin: 0 }}>
                      <Grid item xs={12} lg={6} className={sheetDetailsClasses.zeroPaddingTop}>
                        <div className={classes.labelled}>
                          <div>
                            <Typography variant="body1" color="textSecondary">
                              Travel Time to Job:
                            </Typography>
                            <DurationInput title="Travel time" {...durationProps('travelTimeTo')} />
                          </div>
                          <div>
                            <Typography variant="body1" color="textSecondary">
                              Travel Time from Job:
                            </Typography>
                            <DurationInput
                              title="Travel time"
                              {...durationProps('travelTimeFrom')}
                            />
                          </div>
                        </div>
                      </Grid>
                    </Grid>
                    {showMiscStipend && (
                      <Grid
                        container
                        lg={12}
                        spacing={4}
                        style={{ position: 'relative', margin: 0 }}
                      >
                        <Grid item xs={12} className={sheetDetailsClasses.zeroPaddingTop}>
                          <div className={classes.labelled}>
                            <div>
                              <Typography variant="body1" color="textSecondary">
                                Misc. Stipend:
                              </Typography>
                              <DurationInput title="Misc. stipend" {...durationProps('rainPay')} />
                            </div>
                            <div className={classes.wide}>
                              <Typography variant="body1" color="textSecondary">
                                Misc. Stipend Description:
                              </Typography>
                              <div className={classnames(sheetDetailsClasses.alignTop)}>
                                <div>
                                  <Controller
                                    name="miscStipendDescription"
                                    control={control}
                                    defaultValue={sheet.rainPay?.description || ''}
                                    as={
                                      <TextField
                                        className={sheetDetailsClasses.textField}
                                        error={Boolean(errors.miscStipendDescription)}
                                        variant="outlined"
                                        inputProps={{
                                          'aria-label': 'Travel time hours',
                                        }}
                                        fullWidth
                                      />
                                    }
                                  />
                                  {errors.miscStipendDescription ? (
                                    <div className={classes.error}>
                                      {errors.miscStipendDescription?.message}
                                    </div>
                                  ) : null}
                                </div>
                              </div>
                            </div>
                          </div>
                        </Grid>
                      </Grid>
                    )}
                  </Grid>
                </div>

                <div className={sheetFormClasses.buttonHolder}>
                  <Button
                    color="secondary"
                    variant="outlined"
                    className={sheetFormClasses.cancelButton}
                    onClick={closeDrawer}
                    disableTouchRipple
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    color="primary"
                    variant="outlined"
                    className={sheetFormClasses.saveButton}
                    disableTouchRipple
                    disabled={errors && !!Object.keys(errors).length}
                  >
                    Save
                  </Button>
                </div>
              </div>
            </form>
          </PageLoader>
        )}
      </div>
      {infoModal.isOpen && (
        <AlertModal isOpen={infoModal.isOpen} info={infoModal.text} onClose={closeModal} />
      )}
      {overlappingModal && (
        <OverlappingModal
          data={overlappingModal.data}
          message={overlappingModal.reason}
          onClose={closeOverlappingModal}
          type={overlappingModal?.type}
        />
      )}
    </>
  );
};

TimeForm.propTypes = {
  closeDrawer: propTypes.func.isRequired,
  openSnackbar: propTypes.func.isRequired,
  sheet: propTypes.object.isRequired,
};

export default TimeForm;
